import React, { useState, useEffect } from 'react';
import { LayoutTemplate } from 'lucide-react';
import PageThumbnailPreview from '../Editor/PageThumbnailPreview';

/**
 * Shared template thumbnail — used across editor, public site, admin, and designer.
 *
 * Rendering layers (back → front):
 *   1. PageThumbnailPreview  — CSS live render, always visible when template_json.pages exists.
 *      Never blank, no CORS issues, renders background image / color / elements.
 *   2. `fallback` slot       — shown only when neither (1) nor (3) can render.
 *      Defaults to a generic LayoutTemplate icon; designer pages pass a colored
 *      initial tile, etc. Always behind any stored raster overlay.
 *   3. Stored raster img     — `template.thumbnail || template.preview_url`,
 *      tried in that order. Overlaid on top once loaded.
 *      Hidden (opacity:0) until we verify it is not a blank/white html2canvas artefact.
 *      If blank-detection passes → becomes visible (covers the CSS render for higher quality).
 *      If blank or fails to load → next candidate is tried; final failure → layer hides
 *      and the CSS render or fallback shows through.
 *
 * Blank detection: samples an 8×8 downscale of the thumbnail on a canvas.
 * If ≥85% of pixels are near-white (>245,>245,>245) the thumbnail is considered
 * blank and is discarded.  CORS-blocked canvases (external CDN thumbs) are treated
 * as valid (non-blank) so they still show.
 */

/**
 * "Prefer live render" heuristic — returns true when the page contains element
 * types that the now-removed simplified renderers (TemplateCanvasPreview,
 * CanvasPreview in AdminTemplateGenerator) did NOT handle. Those renderers
 * baked broken stored thumbnails into Mongo before the unification fix; until
 * those records are re-generated, we must hide the stored raster and let the
 * canonical PageThumbnailPreview render below show through.
 *
 * Detected complexity (any one is sufficient):
 *   - SVG icon elements (old renderer returned null)
 *   - QR code elements (old renderer returned null)
 *   - Clipped / mask elements (old renderer ignored clip-path)
 *   - Text with gradient fill or stroke or stroke width (old renderer dropped them)
 *   - Non-trivial shape (anything that isn't a circle/rect — old renderer flattened
 *     to a div with borderRadius and lost the actual silhouette)
 *   - Background blur (old renderer skipped the blur filter)
 *
 * Simple pages (plain text + image + circle/rect shapes only) are NOT flagged
 * — their stored thumbnails are reliable and we keep using them as a sharper
 * overlay.
 */
const _SIMPLE_SHAPES = new Set(['circle', 'rect', 'roundedRect']);
const _pagePrefersLiveRender = (page) => {
  if (!page) return false;
  if ((page.backgroundConfig?.blur || 0) > 0) return true;
  const els = page.elements || [];
  for (const el of els) {
    if (!el || el.visible === false) continue;
    if (el.type === 'svg' || el.type === 'qrcode') return true;
    if (el.clippedTo || el.isMaskShape || el.isClipBase) return true;
    if (el.type === 'text') {
      if (el.fillGradient?.colors?.length > 1) return true;
      if (el.strokeGradient?.colors?.length > 1) return true;
      if (Number(el.strokeWidth) > 0) return true;
    }
    if (el.type === 'shape' && el.shape && !_SIMPLE_SHAPES.has(el.shape)) return true;
  }
  return false;
};

const _checkBlank = (imgEl) => {
  try {
    const c = document.createElement('canvas');
    c.width = 8; c.height = 8;
    const ctx = c.getContext('2d');
    ctx.drawImage(imgEl, 0, 0, 8, 8);
    const d = ctx.getImageData(0, 0, 8, 8).data; // throws on CORS-tainted canvas
    let white = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] > 245 && d[i + 1] > 245 && d[i + 2] > 245) white++;
    }
    return white > 54; // >85% of 64 sampled pixels are white → blank
  } catch {
    return false; // canvas tainted (CORS) → assume valid, show the thumbnail
  }
};

const TemplateThumbnail = ({
  template,
  className = '',
  imgClassName = '',
  onClick,
  children,
  fallback = null,
}) => {
  const w = template.canvas_width  || template.width  || 1080;
  const h = template.canvas_height || template.height || 1080;

  const firstPage = template.template_json?.pages?.[0] ?? null;

  // If the page contains anything the old broken renderers couldn't draw,
  // hide the stored raster entirely — its baked output is suspect.
  // The canonical PageThumbnailPreview underneath handles everything.
  const preferLive = _pagePrefersLiveRender(firstPage);

  // Stored-raster candidate chain: `thumbnail` first, then `preview_url`.
  // Designer submissions populate both; older admin records may only have one.
  const candidates = preferLive
    ? []
    : [template.thumbnail, template.preview_url].filter(Boolean);
  const [candIdx, setCandIdx] = useState(0);
  useEffect(() => { setCandIdx(0); }, [template.id]);
  const thumbSrc = candIdx < candidates.length ? candidates[candIdx] : null;

  // 'pending' → still loading  |  'ok' → show  |  'skip' → blank/broken, hide
  const [thumbState, setThumbState] = useState('pending');
  useEffect(() => { setThumbState('pending'); }, [template.id, thumbSrc]);

  const showThumb = thumbState === 'ok' && thumbSrc;

  // Item 8 — report the actual thumbnail source. Enable in the browser console
  // with: window.__THUMB_SOURCE_LOG__ = true   (then re-open the surface).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.__THUMB_SOURCE_LOG__) return;
    const source = showThumb
      ? `stored-raster:${thumbSrc === template.thumbnail ? 'template.thumbnail' : 'template.preview_url'}`
      : (preferLive ? 'live-render (preferLive: gradient/qr/svg/clip/blur)' : 'live-render (no usable stored raster)');
    // eslint-disable-next-line no-console
    console.log('thumbnail source', {
      id: template.id, source, preferLive,
      hasThumbnail: !!template.thumbnail, hasPreviewUrl: !!template.preview_url,
    });
  }, [showThumb, preferLive, thumbSrc, template.id, template.thumbnail, template.preview_url]);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        width: '100%',
        aspectRatio: `${w} / ${h}`,
        backgroundColor: '#f5f5f5',
        borderRadius: 'inherit',
      }}
      onClick={onClick}
    >
      {/* Layer 1 (base): live CSS render — always present when template_json exists.
          Clips to canvas: anything outside page bounds is dropped (matches export). */}
      {firstPage && (
        <PageThumbnailPreview
          page={firstPage}
          canvasSize={{ width: w, height: h }}
          fillWidth={true}
        />
      )}

      {/* Fallback when no template_json: caller-supplied slot or generic icon */}
      {!firstPage && !showThumb && (
        fallback != null ? fallback : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <LayoutTemplate className="w-10 h-10 text-gray-300" />
          </div>
        )
      )}

      {/* Layer 2 (top): stored thumbnail — invisible until blank-check passes.
          Skip the overlay when the source is smaller than the displayed area
          (AI templates store ~124×155 jpegs that would upscale to ~416px in the
          preview modal and look blurry). The live CSS render below stays sharp. */}
      {thumbSrc && thumbState !== 'skip' && (
        <img
          key={thumbSrc}
          src={thumbSrc}
          alt={template.name || ''}
          crossOrigin="anonymous"
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            const img = e.target;
            if (_checkBlank(img)) {
              // Try the next candidate (e.g. preview_url after thumbnail).
              if (candIdx + 1 < candidates.length) setCandIdx(candIdx + 1);
              else setThumbState('skip');
              return;
            }
            // Compare source resolution to the ON-SCREEN PIXEL width — i.e.
            // CSS width × devicePixelRatio. On retina (DPR=2) a 124px sidebar
            // tile renders ~248 device pixels; a 200px stored thumbnail would
            // still upscale and look blurry next to the sharp CSS render
            // underneath. Skip the raster overlay whenever the source can't
            // match the device-pixel target at ≥ 1.0x.
            const cssW = img.offsetWidth || 0;
            const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
            const targetW = cssW * dpr;
            if (targetW > 0 && img.naturalWidth > 0 && img.naturalWidth < targetW) {
              setThumbState('skip');
              return;
            }
            setThumbState('ok');
          }}
          onError={() => {
            if (candIdx + 1 < candidates.length) setCandIdx(candIdx + 1);
            else setThumbState('skip');
          }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ${imgClassName}`}
          style={{ opacity: showThumb ? 1 : 0, imageRendering: 'auto' }}
        />
      )}

      {/* Overlay slot — badges, hover CTAs, etc. */}
      {children}
    </div>
  );
};

export default TemplateThumbnail;
